import { Controller } from "@hotwired/stimulus"
import { readFilterParams, writeFilterParams } from "../utils/url_filters"

const URL_KEYS = ["category_filters", "date_from", "date_to", "completion"]

export default class extends Controller {
  static targets = ["allButton", "categoryFilter", "categoryCount", "categoryDropdown",
                     "dateButton", "dateIndicator", "datePickerWrapper", "datePickerCalendar",
                     "completionButton"]

  connect() {
    this.activeCategoryFilters = []
    this.dateFrom = ""
    this.dateTo = ""
    this.completionFilter = ""
    this.boundCloseDatePicker = this.closeDatePickerOutside.bind(this)
    this.restoreFromUrl()
    this.updateAllButtonStyle()

    if (this.hasAnyFilter()) {
      setTimeout(() => this.dispatchFilters(), 0)
    }
  }

  restoreFromUrl() {
    const params = readFilterParams(URL_KEYS)

    if (params.category_filters) {
      this.activeCategoryFilters = params.category_filters.split(",")
      this.categoryFilterTargets.forEach(cb => {
        if (this.activeCategoryFilters.includes(cb.getAttribute("data-category-name"))) {
          cb.checked = true
        }
      })
      this.updateCategoryCount()
    }

    if (params.date_from && params.date_to) {
      this.dateFrom = params.date_from
      this.dateTo = params.date_to
      if (this.hasDatePickerCalendarTarget) {
        this.datePickerCalendarTarget.dataset.dateFrom = params.date_from
        this.datePickerCalendarTarget.dataset.dateTo = params.date_to
      }
      this.updateDateIndicator()
    }

    if (params.completion) {
      this.completionFilter = params.completion
      this.updateCompletionButtons()
    }
  }

  hasAnyFilter() {
    return this.activeCategoryFilters.length > 0 ||
      (this.dateFrom && this.dateTo) ||
      this.completionFilter !== ""
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDatePicker)
  }

  toggleCategory(event) {
    const name = event.currentTarget.getAttribute("data-category-name")

    if (event.currentTarget.checked) {
      if (!this.activeCategoryFilters.includes(name)) {
        this.activeCategoryFilters.push(name)
      }
    } else {
      this.activeCategoryFilters = this.activeCategoryFilters.filter(f => f !== name)
    }

    this.updateCategoryCount()
    this.updateAllButtonStyle()
    this.dispatchFilters()
  }

  toggleDatePicker(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasDatePickerCalendarTarget) return

    const isOpen = this.datePickerCalendarTarget.classList.contains("open")

    if (isOpen) {
      this.closeDatePicker()
    } else {
      this.datePickerCalendarTarget.classList.add("open")

      const pickerController = this.application.getControllerForElementAndIdentifier(
        this.datePickerCalendarTarget, "date-range-picker"
      )
      if (pickerController) pickerController.open()

      document.addEventListener("click", this.boundCloseDatePicker)
    }
  }

  applyDateRange(event) {
    event.preventDefault()
    if (!this.hasDatePickerCalendarTarget) return

    const dateFrom = this.datePickerCalendarTarget.dataset.dateFrom
    const dateTo = this.datePickerCalendarTarget.dataset.dateTo
    if (!dateFrom || !dateTo) return

    this.dateFrom = dateFrom
    this.dateTo = dateTo
    this.updateDateIndicator()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  clearDateRange(event) {
    event.preventDefault()

    const pickerController = this.application.getControllerForElementAndIdentifier(
      this.datePickerCalendarTarget, "date-range-picker"
    )
    if (pickerController) pickerController.clear()

    this.dateFrom = ""
    this.dateTo = ""
    this.updateDateIndicator()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  toggleCompletion(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.completionValue

    if (this.completionFilter === value) {
      this.completionFilter = ""
    } else {
      this.completionFilter = value
    }

    this.updateCompletionButtons()
    this.updateAllButtonStyle()
    this.dispatchFilters()
  }

  resetFilters(event) {
    event.preventDefault()
    this.activeCategoryFilters = []
    this.dateFrom = ""
    this.dateTo = ""
    this.completionFilter = ""
    this.categoryFilterTargets.forEach(cb => { cb.checked = false })

    if (this.hasDatePickerCalendarTarget) {
      const pickerController = this.application.getControllerForElementAndIdentifier(
        this.datePickerCalendarTarget, "date-range-picker"
      )
      if (pickerController) pickerController.clear()
    }

    this.updateCategoryCount()
    this.updateDateIndicator()
    this.updateCompletionButtons()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  dispatchFilters() {
    this.dispatch("filtersChanged", {
      detail: {
        categoryFilters: this.activeCategoryFilters.join(","),
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        completion: this.completionFilter
      }
    })
    this.updatePdfExportLinks()
    this.updateUrl()
  }

  updateUrl() {
    writeFilterParams({
      category_filters: this.activeCategoryFilters.join(","),
      date_from: this.dateFrom,
      date_to: this.dateTo,
      completion: this.completionFilter
    })
  }

  updatePdfExportLinks() {
    document.querySelectorAll("[data-pdf-export-link]").forEach((link) => {
      const url = new URL(link.href)
      const categories = this.activeCategoryFilters.join(",")

      this.setOrDeleteParam(url, "category_filters", categories)
      this.setOrDeleteParam(url, "date_from", this.dateFrom)
      this.setOrDeleteParam(url, "date_to", this.dateTo)
      this.setOrDeleteParam(url, "completion", this.completionFilter)

      link.href = url.toString()
    })
  }

  setOrDeleteParam(url, key, value) {
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
  }

  closeDatePickerOutside(event) {
    if (!this.hasDatePickerWrapperTarget) return
    if (this.datePickerWrapperTarget.contains(event.target)) return
    this.closeDatePicker()
  }

  closeDatePicker() {
    if (this.hasDatePickerCalendarTarget) {
      this.datePickerCalendarTarget.classList.remove("open")
    }
    document.removeEventListener("click", this.boundCloseDatePicker)
  }

  updateCategoryCount() {
    if (!this.hasCategoryCountTarget) return

    const count = this.activeCategoryFilters.length
    if (count > 0) {
      this.categoryCountTarget.textContent = count
      this.categoryCountTarget.classList.remove("d-none")
      if (this.hasCategoryDropdownTarget) this.categoryDropdownTarget.classList.add("is-active")
    } else {
      this.categoryCountTarget.classList.add("d-none")
      if (this.hasCategoryDropdownTarget) this.categoryDropdownTarget.classList.remove("is-active")
    }
  }

  updateDateIndicator() {
    if (!this.hasDateIndicatorTarget || !this.hasDateButtonTarget) return

    if (this.dateFrom && this.dateTo) {
      this.dateIndicatorTarget.textContent = "●"
      this.dateIndicatorTarget.classList.remove("d-none")
      this.dateButtonTarget.classList.add("is-active")
    } else {
      this.dateIndicatorTarget.classList.add("d-none")
      this.dateButtonTarget.classList.remove("is-active")
    }
  }

  updateCompletionButtons() {
    this.completionButtonTargets.forEach(btn => {
      if (btn.dataset.completionValue === this.completionFilter) {
        btn.classList.add("is-active")
      } else {
        btn.classList.remove("is-active")
      }
    })
  }

  updateAllButtonStyle() {
    const hasAnyFilter = this.activeCategoryFilters.length > 0 ||
      (this.dateFrom && this.dateTo) ||
      this.completionFilter !== ""

    if (hasAnyFilter) {
      this.allButtonTarget.classList.remove("text-disabled-green")
      this.allButtonTarget.classList.add("text-green-dark-background")
    } else {
      this.allButtonTarget.classList.add("text-disabled-green")
      this.allButtonTarget.classList.remove("text-green-dark-background")
    }
  }
}
